import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { decryptPDF } from '../../../core/workbook/pdfDecryptor'; // 🆕 ایمپورت رمزگشا
import { loadPdfFromMemory } from '../../../core/workbook/pdfiumEngine';
import { initSecurityLayer } from '../../../core/workbook/security';
import { saveAnnotationsLocal, loadAnnotationsLocal } from '../../../core/workbook/annotationDB';
import { jozveService } from '../../../services/jozveService'; // 🆕 ایمپورت سرویس
import { materialHistoryService } from '../../../services/materialHistoryService';
import SecurePDFCanvas from '../../../components/Workbook/SecurePDFCanvas';
import AnnotationLayer from '../../../components/Workbook/AnnotationLayer';
import FloatingToolbar from '../../../components/Workbook/FloatingToolbar';
import { getToken } from '../../../utils/secureStorage';
import '../../../pages/workbook.css';

const useViewportWidth = () => {
  const [width, setWidth] = useState(() => typeof document !== 'undefined' ? (document.documentElement.clientWidth || window.innerWidth || 0) : 0);
  useLayoutEffect(() => {
    let raf = null;
    const update = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const w = document.documentElement.clientWidth || window.innerWidth || 0;
        if (w > 0) setWidth((prev) => (prev !== w ? w : prev));
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    let ro = null;
    try { ro = new ResizeObserver(update); ro.observe(document.documentElement); } catch {}
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      if (ro) ro.disconnect();
    };
  }, []);
  return width;
};

function PdfPage({ pageNumber, pdfDocument, availableWidth, naturalSize, isActive, initialStrokes, user, annotationsTick, activeTool, toolSettings, eraserMode, onRegisterAPI, onStateChange, onActivate }) {
  const wrapperRef = useRef(null);
  const [shouldRender, setShouldRender] = useState(false);
  const [actualWidth, setActualWidth] = useState(availableWidth > 0 ? availableWidth : 320);

  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setActualWidth(w);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const naturalW = naturalSize?.width > 0 ? naturalSize.width : 595;
  const naturalH = naturalSize?.height > 0 ? naturalSize.height : 842;
  const displayWidth = actualWidth;
  const scale = displayWidth / naturalW;
  const displayHeight = displayWidth * (naturalH / naturalW);
  const isHand = activeTool === 'hand';

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) setShouldRender(true); });
    }, { rootMargin: '700px 0px', threshold: 0.01 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleActivate = useCallback(() => { if (onActivate && !isActive) onActivate(pageNumber); }, [onActivate, pageNumber, isActive]);

  return (
    <div ref={wrapperRef} className={`pdf-page-wrapper ${isActive ? 'is-active' : 'is-inactive'}`} style={{ width: '100%', maxWidth: `${availableWidth}px`, aspectRatio: `${naturalW} / ${naturalH}` }} onPointerDownCapture={handleActivate} data-page={pageNumber}>
      {shouldRender && pdfDocument ? (
        <>
          <SecurePDFCanvas pdfDocument={pdfDocument} pageNumber={pageNumber} scale={scale} userId={user.id} userName={user.fullName || user.name} />
          <div className="annotation-layer-wrap" style={{ pointerEvents: isHand ? 'none' : 'auto' }}>
            <AnnotationLayer key={`ann-${pageNumber}-${annotationsTick}`} pageNumber={pageNumber} width={Math.round(displayWidth)} height={Math.round(displayHeight)} initialStrokes={initialStrokes} onRegisterAPI={(api) => onRegisterAPI(pageNumber, api)} onStateChange={isActive ? onStateChange : undefined} activeTool={activeTool} toolSettings={toolSettings} eraserMode={eraserMode} />
          </div>
        </>
      ) : (
        <div className="page-placeholder"><div className="page-placeholder-spinner" /><span>صفحه {pageNumber}</span></div>
      )}
    </div>
  );
}

const JozweViewer = () => {
  const { materialId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // 🆕 خواندن state از location یا sessionStorage
  const stateFromLocation = location.state || {};
  // 🆕 خواندن از localStorage (ماندگارتر از sessionStorage)
  let stateFromStorage = {};
  try {
    stateFromStorage = JSON.parse(localStorage.getItem(`jozwe-view-${materialId}`) || '{}');
    // پاک کردن رکوردهای خیلی قدیمی (بیش از ۲۴ ساعت)
    if (stateFromStorage.timestamp && Date.now() - stateFromStorage.timestamp > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(`jozwe-view-${materialId}`);
      stateFromStorage = {};
    }
  } catch {}

  const finalState = { ...stateFromStorage, ...stateFromLocation };
  // 🆕 اطمینان از وجود decryptId (fallback به materialId)
  const { fileUrl, title, decryptId: rawDecryptId } = finalState;
  const decryptId = rawDecryptId || materialId;

  const securityRef = useRef(null);
  const scrollRef = useRef(null);
  const apiRef = useRef(null);
  const pageApisRef = useRef(new Map());
  const prevActivePageRef = useRef(1);
  const activePageRef = useRef(1);
  const pdfDocumentRef = useRef(null);

  const [pdfDocument, setPdfDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadPercent, setLoadPercent] = useState(0);
  const [loadPhase, setLoadPhase] = useState('آماده‌سازی...');
  const [error, setError] = useState(null);
  
  const [activePage, setActivePage] = useState(1);
  const [annotations, setAnnotations] = useState({});
  const [annotationsTick, setAnnotationsTick] = useState(0);
  const [viewMode, setViewMode] = useState('single');

  const [activeTool, setActiveTool] = useState('hand');
  const [toolSettings, setToolSettings] = useState({ color: '#000000', size: 3, opacity: 0.35 });
  const [eraserMode, setEraserMode] = useState('area');
  const [saving, setSaving] = useState(false);
  const [apiState, setApiState] = useState({ canUndo: false, canRedo: false });

  const viewportWidth = useViewportWidth();
  const availableWidth = viewportWidth > 0 ? viewportWidth : 320;

  const fetchWithProgress = async (url, onProgress) => {
    const token = getToken();
    const response = await fetch(url, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} });
    if (!response.ok) throw new Error('خطا در دریافت فایل امن');
    
    const contentLength = parseInt(response.headers.get('Content-Length') || '0', 10);
    const reader = response.body.getReader();
    const chunks = [];
    let received = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;
      if (onProgress && contentLength > 0) onProgress(Math.min(100, (received / contentLength) * 100));
    }

    const result = new Uint8Array(received);
    let offset = 0;
    for (const chunk of chunks) { result.set(chunk, offset); offset += chunk.length; }
    return result.buffer;
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (!fileUrl) throw new Error('مسیر فایل مشخص نشده است.');
        materialHistoryService.track(materialId, 'jozve', 'viewed');

        setLoadPhase('دانلود امن فایل...');
        setLoadPercent(10);
        const streamUrl = `/api/secure-jozwe/stream?file=${encodeURIComponent(fileUrl)}`;
        const encryptedBuffer = await fetchWithProgress(streamUrl, (percent) => setLoadPercent(10 + Math.round(percent * 0.5)));

        if (cancelled) return;

        setLoadPhase('رمزگشایی امن...');
        setLoadPercent(60);

        // 🆕 استفاده از روت جدید
        const token = getToken();
        let pdfDataBuffer;
        try {
          // 🆕 استفاده از decryptId که از بالا آمده
          const keyRes = await fetch('/api/secure-jozwe/key', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ bookId: decryptId }),
          });
          if (keyRes.ok) {
            const { key, iv } = await keyRes.json();
            pdfDataBuffer = await decryptPDF(encryptedBuffer, key, iv);
          } else {
            throw new Error('key-fetch-failed');
          }
        } catch (e) {
          console.warn('⚠️ رمزگشایی ناموفق، استفاده از فایل خام:', e);
          pdfDataBuffer = encryptedBuffer;
        }

        if (cancelled) return;
        setLoadPhase('پردازش صفحات...');
        setLoadPercent(85);
        const doc = await loadPdfFromMemory(new Uint8Array(pdfDataBuffer));

        if (cancelled) { try { doc.close(); } catch {} return; }

        pdfDocumentRef.current = doc;
        setPdfDocument(doc);
        setLoadPercent(100);
        
        // 🆕 لود یادداشت‌ها از سرور (اولویت) + لوکال
        try {
          const serverAnns = await jozveService.loadAnnotations(user.id, materialId);
          const map = {};
          serverAnns.forEach(a => { map[a.pageNumber] = a.strokes; });
          
          const localAnns = await loadAnnotationsLocal(user.id, materialId);
          localAnns.forEach(a => { if (!map[a.pageNumber]) map[a.pageNumber] = a.strokes; });
          
          setAnnotations(map);
        } catch {}

        setTimeout(() => { if (!cancelled) setLoading(false); }, 300);
      } catch (err) {
        console.error(err);
        if (!cancelled) { setError(err.message || 'خطا در بارگذاری'); setLoading(false); }
      }
    };
    if (user?.id) load();
    return () => { cancelled = true; };
  }, [materialId, fileUrl, user?.id]);

  useEffect(() => { return () => { if (pdfDocumentRef.current) { try { pdfDocumentRef.current.close(); } catch {} } }; }, []);
  useEffect(() => { if (!securityRef.current) return; return initSecurityLayer(securityRef.current); }, []);

  const savePage = useCallback(async (pageNum, strokes) => {
    if (!user?.id || !materialId) return;
    setAnnotations(prev => ({ ...prev, [pageNum]: strokes }));
    // ذخیره در هر دو لوکال و سرور
    saveAnnotationsLocal(user.id, materialId, pageNum, strokes).catch(() => {});
    jozveService.saveAnnotations(user.id, materialId, pageNum, strokes).catch(() => {});
  }, [user?.id, materialId]);

  const handleSave = useCallback(async (pageNum, strokes) => {
    setSaving(true);
    try { await savePage(pageNum, strokes); } catch {} finally { setSaving(false); }
  }, [savePage]);

  useEffect(() => {
    const prevPage = prevActivePageRef.current;
    if (prevPage === activePage) return;
    const prevApi = pageApisRef.current.get(prevPage);
    const prevStrokes = prevApi?.getStrokes() || [];
    if (prevStrokes.length > 0) savePage(prevPage, prevStrokes);
    prevActivePageRef.current = activePage;
  }, [activePage, savePage]);

  const handleRegisterAPI = useCallback((pageNum, api) => {
    pageApisRef.current.set(pageNum, api);
    if (pageNum === activePageRef.current) apiRef.current = api;
  }, []);

  const handlePageStateChange = useCallback((pageNum, state) => {
    if (pageNum === activePageRef.current) setApiState(state);
  }, []);

  useEffect(() => { apiRef.current = pageApisRef.current.get(activePage) || null; setApiState({ canUndo: false, canRedo: false }); }, [activePage]);
  useEffect(() => { activePageRef.current = activePage; }, [activePage]);

  const switchViewMode = (mode) => {
    if (mode === viewMode) return;
    const currentStrokes = apiRef.current?.getStrokes() || [];
    if (currentStrokes.length > 0) savePage(viewMode === 'single' ? activePage : activePageRef.current, currentStrokes);
    setViewMode(mode);
    setAnnotationsTick(t => t + 1);
  };

  if (loading) {
    const radius = 54; const circumference = 2 * Math.PI * radius; const strokeDashoffset = circumference - (loadPercent / 100) * circumference;
return (
  <div className="wb-loading-screen light-theme" style={{position:'fixed', inset:0, zIndex:9999}}>
    <div className="wb-loading-card">
          <div className="wb-circle-progress">
            <svg width="140" height="140" viewBox="0 0 140 140">
              <circle cx="70" cy="70" r={radius} fill="none" strokeWidth="8" className="wb-circle-bg" />
              <circle cx="70" cy="70" r={radius} fill="none" strokeWidth="8" strokeLinecap="round" className="wb-circle-fill" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} transform="rotate(-90 70 70)" />
            </svg>
            <div className="wb-percent-text"><span className="wb-percent-num">{loadPercent}%</span></div>
          </div>
          <h2 className="wb-loading-title">جزوه امن</h2>
          <p className="wb-loading-phase">{loadPhase}</p>
        </div>
      </div>
    );
  }

  if (error) return (<div className="workbook-error" style={{padding: '40px', textAlign: 'center'}}><span style={{fontSize: '40px'}}>⚠️</span><p>{error}</p><button onClick={() => navigate('/jozwe')} style={{padding: '10px 20px', marginTop: '20px'}}>بازگشت</button></div>);

return (
  <div className="wb-root" ref={securityRef}>
    <div className="workbook-header">
        <div className="workbook-header-left">
          <button className="btn-back" onClick={() => navigate('/jozwe')}>← بازگشت</button>
          <div className="view-mode-toggle" role="group">
            <button className={`mode-btn ${viewMode === 'single' ? 'active' : ''}`} onClick={() => switchViewMode('single')}>تک صفحه</button>
            <button className={`mode-btn ${viewMode === 'continuous' ? 'active' : ''}`} onClick={() => switchViewMode('continuous')}>کشویی</button>
          </div>
        </div>
        <span className="page-indicator">{title || 'مشاهده جزوه'}</span>
        <div className="sync-indicator" />
      </div>
      <div ref={scrollRef} className={`workbook-content ${viewMode === 'continuous' ? 'mode-continuous' : 'mode-single'}`} style={{ overflowY: 'scroll' }}>
        {viewMode === 'single' ? (
          <PdfPage key={`single-${activePage}-${annotationsTick}`} pageNumber={activePage} pdfDocument={pdfDocument} availableWidth={availableWidth} naturalSize={pdfDocument?.pageSizes[activePage - 1] || { width: 595, height: 842 }} isActive={true} initialStrokes={annotations[activePage] || []} user={user} annotationsTick={annotationsTick} activeTool={activeTool} toolSettings={toolSettings} eraserMode={eraserMode} onRegisterAPI={handleRegisterAPI} onStateChange={(state) => handlePageStateChange(activePage, state)} onActivate={() => {}} />
        ) : (
          <div className="continuous-scroll">
            {Array.from({ length: pdfDocument?.pageCount || 0 }, (_, i) => (
              <PdfPage key={`page-${i+1}-${annotationsTick}`} pageNumber={i+1} pdfDocument={pdfDocument} availableWidth={availableWidth} naturalSize={pdfDocument?.pageSizes[i] || { width: 595, height: 842 }} isActive={activePage === i+1} initialStrokes={annotations[i+1] || []} user={user} annotationsTick={annotationsTick} activeTool={activeTool} toolSettings={toolSettings} eraserMode={eraserMode} onRegisterAPI={handleRegisterAPI} onStateChange={(state) => handlePageStateChange(i+1, state)} onActivate={setActivePage} />
            ))}
          </div>
        )}
      </div>
      {viewMode === 'single' && (
        <div className="workbook-nav">
          <button onClick={() => setActivePage(p => Math.max(1, p - 1))} disabled={activePage <= 1}>← قبلی</button>
          <input type="number" min={1} max={pdfDocument?.pageCount || 1} value={activePage} onChange={(e) => setActivePage(+e.target.value)} />
          <button onClick={() => setActivePage(p => Math.min(pdfDocument?.pageCount || 1, p + 1))} disabled={activePage >= (pdfDocument?.pageCount || 1)}>بعدی →</button>
        </div>
      )}
      <FloatingToolbar activeTool={activeTool} setActiveTool={setActiveTool} toolSettings={toolSettings} setToolSettings={setToolSettings} eraserMode={eraserMode} setEraserMode={setEraserMode} onUndo={() => apiRef.current?.undo()} onRedo={() => apiRef.current?.redo()} canUndo={apiState.canUndo} canRedo={apiState.canRedo} onClearAll={() => { apiRef.current?.clearAll(); handleSave(activePage, []); }} onSave={() => handleSave(activePage, apiRef.current?.getStrokes() || [])} saving={saving} />
    </div>
  );
};

export default JozweViewer;